import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
  computed,
  ElementRef,
  ViewChild,
  AfterViewInit,
  DestroyRef,
  input,
  output,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { fromEvent } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  LucideAngularModule,
  Minus,
  Plus,
  Maximize2,
  ExternalLink,
  MessageSquare,
  Loader2,
} from 'lucide-angular';
import {
  LessonSlide,
  SlideThumbnail,
} from '../../../../../services/lesson-plan.models';
import { AuthService } from '../../../../../../../../common/auth/auth.service';
import {
  PricingModalComponent,
  PaywallTarget,
} from '../../../../../../../paywall/pricing-modal/pricing-modal';
import { SubscriptionSuccessModalComponent } from '../../../../../../../paywall/subscription-success-modal/subscription-success-modal';
import { Product } from '../../../../../../../paywall/billing-api.service';

interface RulerTick {
  position: number;
  label: string;
  isMajor: boolean;
}

@Component({
  selector: 'app-slide-canvas',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule, PricingModalComponent, SubscriptionSuccessModalComponent],
  templateUrl: './slide-canvas.html',
  styleUrl: './slide-canvas.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SlideCanvasComponent implements AfterViewInit, OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly authService = inject(AuthService);

  @ViewChild('canvasContainer') canvasContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('slideWrapper') slideWrapper!: ElementRef<HTMLDivElement>;

  readonly slide = input.required<LessonSlide>();
  readonly slideNumber = input.required<number>();
  readonly totalSlides = input.required<number>();
  readonly thumbnail = input<SlideThumbnail | null>(null);
  readonly isLoadingThumbnail = input<boolean>(false);
  readonly presentationUrl = input<string | null>(null);
  readonly simplifiedMode = input<boolean>(false);

  readonly zoomChanged = output<number>();
  readonly leaveFeedback = output<void>();

  // Base slide dimensions (16:9 aspect ratio)
  protected readonly baseSlideWidth = 960;
  protected readonly baseSlideHeight = 540;

  // Zoom state
  protected readonly zoomLevel = signal<number>(70);
  protected readonly zoomLevels = [25, 50, 70, 100, 125, 150, 200];

  // Display zoom levels - includes current zoom if not in predefined list
  protected readonly displayZoomLevels = computed(() => {
    const current = this.zoomLevel();
    if (this.zoomLevels.includes(current)) {
      return this.zoomLevels;
    }
    // Insert current zoom in sorted position
    return [...this.zoomLevels, current].sort((a, b) => a - b);
  });

  // Scroll position for rulers
  protected readonly scrollX = signal<number>(0);
  protected readonly scrollY = signal<number>(0);

  // Computed scaled dimensions
  protected readonly scaledSlideWidth = computed(() =>
    this.baseSlideWidth * this.zoomLevel() / 100
  );

  protected readonly scaledSlideHeight = computed(() =>
    this.baseSlideHeight * this.zoomLevel() / 100
  );

  // The actual slide position is tracked via DOM observation
  protected readonly slideOffsetX = signal<number>(0);
  protected readonly slideOffsetY = signal<number>(0);

  protected readonly rulerOffsetX = computed(() => {
    return this.slideOffsetX() - this.scrollX();
  });

  protected readonly rulerOffsetY = computed(() => {
    return this.slideOffsetY() - this.scrollY();
  });

  protected readonly horizontalTicks = computed((): RulerTick[] => {
    const ticks: RulerTick[] = [];
    const scale = this.zoomLevel() / 100;
    const majorSpacing = 100 * scale; // spacing for numbered ticks
    const minorDivisions = 4; // 4 ticks per major unit
    const tickSpacing = majorSpacing / minorDivisions;
    const slideWidth = this.scaledSlideWidth();

    const totalTicks = Math.ceil(slideWidth / tickSpacing);
    for (let i = 0; i <= totalTicks; i++) {
      const position = i * tickSpacing;
      if (position <= slideWidth) {
        const isNumbered = i % minorDivisions === 0;
        ticks.push({
          position,
          label: isNumbered ? String(i / minorDivisions) : '',
          isMajor: isNumbered,
        });
      }
    }
    return ticks;
  });

  protected readonly verticalTicks = computed((): RulerTick[] => {
    const ticks: RulerTick[] = [];
    const scale = this.zoomLevel() / 100;
    const majorSpacing = 100 * scale; // spacing for numbered ticks
    const minorDivisions = 4; // 4 ticks per major unit
    const tickSpacing = majorSpacing / minorDivisions;
    const slideHeight = this.scaledSlideHeight();

    const totalTicks = Math.ceil(slideHeight / tickSpacing);
    for (let i = 0; i <= totalTicks; i++) {
      const position = i * tickSpacing;
      if (position <= slideHeight) {
        const isNumbered = i % minorDivisions === 0;
        ticks.push({
          position,
          label: isNumbered ? String(i / minorDivisions) : '',
          isMajor: isNumbered,
        });
      }
    }
    return ticks;
  });

  protected readonly icons = {
    Minus,
    Plus,
    Maximize2,
    ExternalLink,
    MessageSquare,
    Loader2,
  };

  // Pricing modal state
  protected readonly isPricingModalOpen = signal<boolean>(false);
  protected readonly paywallTarget: PaywallTarget = 'export_google_slides';

  // Subscription success modal state
  protected readonly isSubscriptionSuccessOpen = signal<boolean>(false);
  protected readonly subscribedProductName = signal<string>('Premium');

  ngOnInit(): void {
    // Ensure user profile is loaded
    this.authService.getProfile().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  /** Check if user has an active premium subscription */
  private hasPremium(): boolean {
    return this.authService.hasPremium();
  }

  // Pricing modal handlers
  protected openPricingModal(): void {
    this.isPricingModalOpen.set(true);
  }

  protected closePricingModal(): void {
    this.isPricingModalOpen.set(false);
  }

  protected onPricingProductSelected(): void {
    // Don't close the pricing modal - let it handle showing the checkout modal internally
  }

  protected onPaymentSuccess(product: Product): void {
    this.isPricingModalOpen.set(false);
    this.subscribedProductName.set(product.name);
    this.isSubscriptionSuccessOpen.set(true);

    // Note: Profile refresh is handled by the subscription success modal's polling logic
    // Execute the pending action after successful subscription
    this.executeOpenInGoogleSlides();
  }

  protected closeSubscriptionSuccess(): void {
    this.isSubscriptionSuccessOpen.set(false);
  }

  protected openInGoogleSlides(): void {
    // If user has premium, open directly without paywall
    if (this.hasPremium()) {
      this.executeOpenInGoogleSlides();
    } else {
      this.openPricingModal();
    }
  }

  private executeOpenInGoogleSlides(): void {
    const url = this.presentationUrl();
    if (url) {
      window.open(url, '_blank');
    }
  }

  protected onLeaveFeedback(): void {
    this.leaveFeedback.emit();
  }

  ngAfterViewInit(): void {
    // Skip initialization for simplified mode (no canvas container)
    if (this.simplifiedMode() || !this.canvasContainer?.nativeElement) {
      return;
    }

    // Wait for layout to complete before measuring positions
    // Double RAF ensures we're after the browser's layout pass
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Auto-fit to window on first load
        this.fitToWindow();
        this.updateSlidePosition();
      });
    });

    fromEvent(window, 'resize')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        requestAnimationFrame(() => {
          this.updateSlidePosition();
        });
      });

    // Pinch-to-zoom on trackpad
    fromEvent<WheelEvent>(this.canvasContainer.nativeElement, 'wheel', { passive: false })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event.ctrlKey || event.metaKey) {
          event.preventDefault();
          this.handlePinchZoom(event.deltaY);
        }
      });
  }

  private handlePinchZoom(deltaY: number): void {
    const currentZoom = this.zoomLevel();
    const sensitivity = 0.5;
    const zoomDelta = -deltaY * sensitivity;
    const newZoom = Math.max(25, Math.min(200, Math.round(currentZoom + zoomDelta)));

    if (newZoom !== currentZoom) {
      this.applyZoomCentered(newZoom);
    }
  }

  private applyZoomCentered(newZoom: number): void {
    const container = this.canvasContainer?.nativeElement;
    if (!container) {
      this.zoomLevel.set(newZoom);
      this.zoomChanged.emit(newZoom);
      return;
    }

    const oldZoom = this.zoomLevel();
    if (oldZoom === newZoom) return;

    const scaleRatio = newZoom / oldZoom;
    const scrollCenterX = container.scrollLeft + container.clientWidth / 2;
    const scrollCenterY = container.scrollTop + container.clientHeight / 2;

    this.zoomLevel.set(newZoom);
    this.zoomChanged.emit(newZoom);

    requestAnimationFrame(() => {
      const newScrollCenterX = scrollCenterX * scaleRatio;
      const newScrollCenterY = scrollCenterY * scaleRatio;

      container.scrollLeft = newScrollCenterX - container.clientWidth / 2;
      container.scrollTop = newScrollCenterY - container.clientHeight / 2;

      this.scrollX.set(container.scrollLeft);
      this.scrollY.set(container.scrollTop);

      this.updateSlidePosition();
    });
  }

  private updateSlidePosition(): void {
    if (this.slideWrapper?.nativeElement && this.canvasContainer?.nativeElement) {
      const slideRect = this.slideWrapper.nativeElement.getBoundingClientRect();
      const containerRect = this.canvasContainer.nativeElement.getBoundingClientRect();

      const scrollLeft = this.canvasContainer.nativeElement.scrollLeft;
      const scrollTop = this.canvasContainer.nativeElement.scrollTop;

      this.slideOffsetX.set(slideRect.left - containerRect.left + scrollLeft);
      this.slideOffsetY.set(slideRect.top - containerRect.top + scrollTop);
    }
  }

  protected onCanvasScroll(event: Event): void {
    const target = event.target as HTMLDivElement;
    this.scrollX.set(target.scrollLeft);
    this.scrollY.set(target.scrollTop);
  }

  protected zoomIn(): void {
    const current = this.zoomLevel();
    const nextLevel = this.zoomLevels.find(l => l > current);
    if (nextLevel) {
      this.applyZoomCentered(nextLevel);
    }
  }

  protected zoomOut(): void {
    const current = this.zoomLevel();
    const prevLevel = [...this.zoomLevels].reverse().find(l => l < current);
    if (prevLevel) {
      this.applyZoomCentered(prevLevel);
    }
  }

  protected setZoom(value: number): void {
    this.applyZoomCentered(value);
  }

  protected fitToWindow(): void {
    if (!this.canvasContainer?.nativeElement) return;

    const rect = this.canvasContainer.nativeElement.getBoundingClientRect();
    const containerW = rect.width - 64; // padding
    const containerH = rect.height - 64;

    const scaleX = containerW / this.baseSlideWidth;
    const scaleY = containerH / this.baseSlideHeight;
    const scale = Math.min(scaleX, scaleY);

    const targetZoom = Math.max(25, Math.min(200, Math.round(scale * 100)));
    this.applyZoomCentered(targetZoom);
  }
}
